/**
 * connectivity.js — Sarabi OS · Stato centralizzato rete & recovery UI.
 *
 * Fornisce lo stato "online" al resto dell'app con:
 *   - Debounce 1500ms prima di dichiarare "offline" (evita flicker
 *     su micro-glitch mobile).
 *   - Segnalazione "syncing" per 3s dopo il recovery (banner verde).
 *   - Hook React `useConnectivity()` + subscribe API imperativa.
 *
 * Usa `lifecycle.js` come sorgente eventi (single source of truth).
 * NON registra ascoltatori duplicati su window; passa attraverso lifecycle.
 */
import { useEffect, useState } from "react";
import { lifecycle } from "./lifecycle";

const OFFLINE_DEBOUNCE_MS = 1500;
const SYNCING_HOLD_MS = 3000;

const _state = {
  online: (typeof navigator !== "undefined") ? navigator.onLine !== false : true,
  syncing: false,
  offlineTimer: null,
  syncingTimer: null,
  since: Date.now(),
};

const _subs = new Set();

function _notify() {
  const snap = getState();
  _subs.forEach((fn) => { try { fn(snap); } catch { /* silent */ } });
}

function _goOnline() {
  if (_state.offlineTimer) { clearTimeout(_state.offlineTimer); _state.offlineTimer = null; }
  const wasOffline = !_state.online;
  _state.online = true;
  _state.since = Date.now();
  if (wasOffline) {
    _state.syncing = true;
    if (_state.syncingTimer) clearTimeout(_state.syncingTimer);
    _state.syncingTimer = setTimeout(() => {
      _state.syncing = false;
      _notify();
    }, SYNCING_HOLD_MS);
  }
  _notify();
}

function _goOffline() {
  if (_state.offlineTimer) return;
  _state.offlineTimer = setTimeout(() => {
    _state.offlineTimer = null;
    _state.online = false;
    _state.since = Date.now();
    _notify();
  }, OFFLINE_DEBOUNCE_MS);
}

// Subscribe ai lifecycle events
lifecycle.on("online", _goOnline);
lifecycle.on("offline", _goOffline);
// Recovery dopo suspend lungo: se online ma stavamo suspend > 5s → syncing banner breve.
lifecycle.on("recovery", (ctx) => {
  if (ctx && ctx.suspendedForMs > 5000 && _state.online) {
    _state.syncing = true;
    if (_state.syncingTimer) clearTimeout(_state.syncingTimer);
    _state.syncingTimer = setTimeout(() => {
      _state.syncing = false;
      _notify();
    }, SYNCING_HOLD_MS);
    _notify();
  }
});

export function getState() {
  return {
    online: _state.online,
    syncing: _state.syncing,
    since: _state.since,
  };
}

export function subscribe(fn) {
  _subs.add(fn);
  try { fn(getState()); } catch { /* silent */ }
  return () => _subs.delete(fn);
}

/**
 * Hook React che re-renderizza al cambio di stato connettività.
 *   const { online, syncing } = useConnectivity();
 */
export function useConnectivity() {
  const [snap, setSnap] = useState(() => getState());
  useEffect(() => subscribe(setSnap), []);
  return snap;
}
